<template>
  <div class="integration-logs">
    <b-alert variant="info" show class="integration-logs__note">
      {{
        $t(
          "integration.logs_failures_note",
          "Failed exports have only been recorded since this feature was released — earlier periods list successes only."
        )
      }}
    </b-alert>

    <b-form-group
      :label="$t('integration.logs_campaign_filter', 'Filter by campaign')"
      class="integration-logs__filter"
    >
      <multiselect
        v-model="selectedCampaign"
        :options="campaignOptions"
        :allow-empty="true"
        track-by="id"
        :custom-label="(c) => `[${c.id}] ${c.title}`"
        :placeholder="$t('integration.logs_campaign_all', 'All campaigns')"
      />
    </b-form-group>

    <b-alert v-if="errorMessage" variant="danger" show>{{ errorMessage }}</b-alert>

    <div v-else-if="!loading && !rows.length" class="integration-logs__empty">
      {{ $t("integration.logs_empty", "No exports have been recorded for this integration yet.") }}
    </div>

    <template v-else>
      <b-table
        head-variant="light"
        :busy="loading"
        :items="rows"
        :fields="fields"
        :tbody-tr-class="rowClass"
        responsive
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <template #cell(successful)="data">
          <b-badge :variant="data.item.successful ? 'success' : 'danger'">
            {{
              data.item.successful
                ? $t("integration.logs_success", "Success")
                : $t("integration.logs_failure", "Failed")
            }}
          </b-badge>
        </template>

        <template #cell(reason)="data">
          <template v-if="!data.item.successful">
            <div class="integration-logs__reason">{{ data.item.error || "-" }}</div>
            <button
              v-if="data.item.response"
              class="btn btn-link btn-sm p-0"
              @click.prevent="data.toggleDetails"
            >
              {{
                data.detailsShowing
                  ? $t("integration.logs_hide_details", "Hide details")
                  : $t("integration.logs_show_details", "Show details")
              }}
            </button>
          </template>
          <template v-else>-</template>
        </template>

        <template #row-details="data">
          <pre class="integration-logs__details">{{ data.item.response }}</pre>
        </template>
      </b-table>

      <b-pagination
        v-model="page"
        :total-rows="total"
        :per-page="perPage"
        size="sm"
        align="center"
      />
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import Multiselect from "vue-multiselect";

export default {
  name: "IntegrationLogs",

  components: {
    Multiselect,
  },

  props: {
    service: {
      type: String,
      default: "salesforce",
    },
  },

  data() {
    return {
      loading: false,
      errorMessage: "",
      rows: [],
      total: 0,
      page: 1,
      // `per_page` is capped at 100 server side.
      perPage: 25,
      selectedCampaign: null,
      fields: [
        {
          key: "created_at",
          label: this.$t("integration.logs_when", "When"),
          formatter: (value) => moment(value).format("MMMM D, YYYY h:mm a"),
        },
        {
          key: "source_entity_id",
          label: this.$t("integration.logs_donation", "Donation"),
        },
        {
          key: "destination_entity_type",
          label: this.$t("integration.logs_target", "Target"),
        },
        {
          key: "successful",
          label: this.$t("integration.logs_result", "Result"),
        },
        {
          key: "reason",
          label: this.$t("integration.logs_reason", "Reason"),
        },
      ],
    };
  },

  computed: {
    ...mapState({
      campaigns: (state) => state.campaigns.campaigns,
    }),

    campaignOptions() {
      return this.campaigns.map((c) => ({ id: Number(c.id), title: c.attributes.title }));
    },
  },

  watch: {
    page() {
      this.fetchLogs();
    },

    selectedCampaign() {
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.fetchLogs();
    },
  },

  mounted() {
    this.$store.dispatch("getCampaigns");
    this.fetchLogs();
  },

  methods: {
    fetchLogs() {
      this.loading = true;
      this.errorMessage = "";

      this.$store
        .dispatch("fetchIntegrationLogs", {
          service: this.service,
          campaignId: this.selectedCampaign?.id,
          page: this.page,
          perPage: this.perPage,
        })
        .then(({ list, total }) => {
          this.rows = list.map(this.toRow);
          this.total = total;
        })
        .catch((error) => {
          this.rows = [];
          this.total = 0;
          this.errorMessage = this.$_notificationsMixin_string(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // `meta` arrives as a JSON string; a malformed one must not break the table.
    toRow(item) {
      let meta = {};
      try {
        meta = JSON.parse(item.meta) || {};
      } catch {
        meta = {};
      }

      return {
        ...item,
        error: meta.error || "",
        response: meta.response || "",
        _showDetails: false,
      };
    },

    rowClass(item) {
      return item && !item.successful ? "table-danger" : "";
    },
  },
};
</script>

<style lang="scss" scoped>
.integration-logs__filter {
  max-width: 400px;
}

.integration-logs__empty {
  padding: 20px 0;
  text-align: center;
  color: #6c757d;
}

.integration-logs__reason {
  word-break: break-word;
  max-width: 380px;
}

.integration-logs__details {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  margin: 0;
}
</style>
